import React from 'react';

import { useIntl, defineMessages, type IntlShape } from 'react-intl';
import { graphql, useLazyLoadQuery } from 'react-relay';

import PlatformAvatarGroup from '@atlaskit/avatar-group/avatar-group';
import PlatformBadge from '@atlaskit/badge/badge';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box } from '@atlaskit/primitives/box';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { xcss } from '@atlaskit/primitives/xcss/xcss';
import type { RenderFn } from '@atlassian/forge-ui-types/components';
import type { UserProps } from '@atlassian/forge-ui-types/public/components';

import { useInlineContext } from '../../../context/inline';
import { RelaySuspensefulBoundary } from '../../../utils/RelaySuspensefulBoundary';
import { type UserData, type Users } from '../../../web-client/hooks/useUser';
import { type usergroup_forge_ui_usersQuery as usersQuery } from './__generated__/usergroup_forge_ui_usersQuery.graphql';

type AKAGData = { key: string; name: string; src: string };
export const usersToPlatformAvatarStackData = (
	users: Record<string, UserData>,
	accountIds: string[],
	intl: IntlShape,
): AKAGData[] => {
	// order should match accountIds
	return accountIds.map((key) => {
		const { name = intl.formatMessage(messages.unknownUserMessage), picture = '' } =
			users[key] || {};
		return { key, name, src: picture };
	});
};

export const AvatarStack = ({ data }: { data: AKAGData[] }): React.JSX.Element => {
	return <PlatformAvatarGroup appearance="stack" data={data} />;
};

export const More = ({ data }: { data: AKAGData[] }): React.JSX.Element => {
	return <PlatformBadge>{`+${data.length}`}</PlatformBadge>;
};

export const UserGroupInline = ({ data }: { data: AKAGData[] }): React.JSX.Element => {
	const end = Math.max(Math.min(8, data.length), 0); // 4 would be the same as AvatarStack
	const shown = data.slice(0, end);
	const more = data.slice(end);
	const hasMore = more.length > 0;

	return (
		<span>
			{shown.map(({ name }: AKAGData, i) => {
				return (
					<>
						<PlatformBadge appearance="primary">{name}</PlatformBadge>
						{hasMore || i !== end - 1 ? ' ' : null}
					</>
				);
			})}
			{hasMore ? <More data={more} /> : null}
		</span>
	);
};

// @ts-ignore setting z index to 0 so that the avatars do not overlap other components
const avatarStackWrapper = xcss({ zIndex: 0, position: 'relative' });

export const PlatformUserGroup = ({ accountIds }: { accountIds: string[] }): React.JSX.Element => {
	const nonEmptyAccountIds = accountIds.filter((id) => !(id === ''));

	const data = useLazyLoadQuery<usersQuery>(
		graphql`
			query usergroup_forge_ui_usersQuery($accountIds: [ID!]!) {
				users(accountIds: $accountIds) {
					name
					accountId
					picture
				}
			}
		`,
		{
			accountIds: nonEmptyAccountIds,
		},
	);

	let users: Users = {};
	if (data && data.users) {
		users = data.users.reduce<Users>((prev, user) => {
			if (user.accountId) {
				prev[user.accountId] = {
					picture: user.picture,
					accountId: user.accountId,
					name: user.name,
				};
			}
			return prev;
		}, {});
	}

	const intl = useIntl();
	const avatarStackData = usersToPlatformAvatarStackData(users, accountIds, intl);
	const { inline } = useInlineContext();

	if (inline) {
		return <UserGroupInline data={avatarStackData} />;
	}

	return (
		// Each avatar in an avatar stack has a z index value applied.
		// Setting a container with a z index here to 0 so that the avatars do no overlap other components such as the Select menu
		<Box xcss={avatarStackWrapper}>
			<AvatarStack data={avatarStackData} />
		</Box>
	);
};

export const UserGroup = (props: Parameters<RenderFn>[0]): React.JSX.Element => {
	const accountIds = props.forgeDoc.children
		.map((child) => {
			if (child.type !== 'User' && child.type !== 'Avatar') {
				return '';
			}

			const { props } = child;
			const { accountId } = props as UserProps;
			return accountId;
		})
		.filter((x) => x);

	return (
		<RelaySuspensefulBoundary>
			<PlatformUserGroup accountIds={accountIds} />
		</RelaySuspensefulBoundary>
	);
};

const messages = defineMessages({
	unknownUserMessage: {
		id: 'confluence.user.group.unknown.user.message',
		defaultMessage: 'Unknown User',
		description: "This is a fallback message that is visible when a user's name is unknown.",
	},
});
